import { LeagueHomeCard } from "@app/league/components/LeagueHomeCard";
import { type UserHomeResponse } from "@core/home/types";
import type { LeagueSummary } from "@core/league/types";
import { trans, plainTrans } from "@mongez/localization";
import { PinIcon } from "lucide-react";
import { AssessmentsSection } from "../../assessments/components/AssessmentsSection";
import { AnnouncementPanel } from "../../shared/components/AnnouncementPanel";
import { CategoriesSection } from "../components/CategoriesSection";
import { HeroSection } from "../components/HeroSection";
import { LatestLessonsSection } from "../components/LatestLessonsSection";
import { LessonsCarousel } from "../components/LessonsCarousel";
import { OffersCarousel } from "../components/OffersCarousel";
import { PopularLessonsSection } from "../components/PopularLessonsSection";
import { TrainingGroundHomeCard } from "../components/TrainingGroundHomeCard";
import type { TrainingGroundHomeData } from "../utils/training-ground-home";

interface UserHomePageContentProps {
  data: UserHomeResponse;
  trainingGroundHome?: TrainingGroundHomeData;
  leagueHome?: LeagueSummary;
}

export function UserHomePageContent({
  data,
  trainingGroundHome,
  leagueHome,
}: UserHomePageContentProps) {
  return (
    <div className="container mx-auto space-y-12 px-4 py-8 sm:px-6 lg:px-8">
      {/* Hero Section */}
      <HeroSection
        heroImage={data.heroImage}
        hero={data.hero}
        isGuest={false}
      />

      <LeagueHomeCard data={leagueHome} />

      <TrainingGroundHomeCard data={trainingGroundHome} />

      {/* Announcements */}
      {data.announcements?.length > 0 &&
        data.announcements.map((announcement) => (
          <AnnouncementPanel
            key={announcement.id}
            announcement={announcement}
          />
        ))}

      {/* Special Offers Section */}
      {data.grades?.offers && data.grades.offers.length > 0 && (
        <OffersCarousel
          offers={data.grades.offers}
          title={plainTrans("home.specialOffers")}
          description={trans("home.offersDescription")}
        />
      )}

      {/* Important exams - time-sensitive academic content */}
      {data.grades?.assessments && (
        <AssessmentsSection assessments={data.grades.assessments} />
      )}

      {/* Highlighted content for immediate engagement */}
      {data.grades?.pinnedLessons && data.grades.pinnedLessons.length > 0 && (
        <LessonsCarousel
          title={plainTrans("home.pinnedLessons")}
          lessons={data.grades.pinnedLessons}
          icon={<PinIcon className="h-6 w-6 text-orange-600" />}
        />
      )}

      {/* Browse by topic - after showing what's available */}
      {data.grades?.categories && data.grades.categories.length > 0 && (
        <>
          <div className="border-t border-gray-100 dark:border-gray-700" />
          <CategoriesSection categories={data.grades.categories} />
        </>
      )}

      {/* Popular content for social proof */}
      {data.grades?.popularLessons && data.grades.popularLessons.length > 0 && (
        <PopularLessonsSection lessons={data.grades.popularLessons} />
      )}

      {/* Fresh content to encourage exploration */}
      {data.grades?.latestLessons && data.grades.latestLessons.length > 0 && (
        <LatestLessonsSection
          lessons={data.grades.latestLessons}
          iconColor="text-yellow-400"
        />
      )}

      {/* Subjects Section - if available */}
      {data.grades?.subjects && data.grades.subjects.length > 0 && (
        <>
          <div className="border-t border-gray-100 dark:border-gray-700" />
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 sm:text-3xl">
                {trans("home.exploreSubjects")}
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                {trans("home.subjectsDescription")}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {data.grades.subjects.map((subject) => (
                <div
                  key={subject.id}
                  className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white transition-all duration-300 hover:scale-[1.025] dark:border-gray-700 dark:bg-gray-800"
                >
                  {/* Image Section */}
                  <div className="dark:from-primary-950 relative aspect-video w-full overflow-hidden bg-gradient-to-br from-primary-50 to-primary-100 dark:to-primary-900">
                    {subject.image?.url ? (
                      <img
                        src={subject.image.url}
                        alt={subject.name}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center">
                        <div className="h-16 w-16 text-primary-400">
                          <svg fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="p-6">
                    <h3 className="mb-2 line-clamp-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                      {subject.name}
                    </h3>
                    <p className="mb-4 line-clamp-3 text-sm text-gray-600 dark:text-gray-300">
                      {subject.description || trans("home.exploreSubject")}
                    </p>

                    {/* Stats */}
                    {subject.total && (
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <svg
                            className="h-4 w-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>
                            {subject.total.lessons} {trans("common.lessons")}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <svg
                            className="h-4 w-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                          </svg>
                          <span>
                            {subject.total.categories}{" "}
                            {trans("common.categories")}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Grades Categories Section */}
      {/* {data.grades?.gradesCategories &&
        data.grades.gradesCategories.length > 0 && (
          <>
            <div className="border-t border-gray-100 dark:border-gray-700" />
            <GradesCategoriesSection
              gradesCategories={data.grades.gradesCategories}
              className="space-y-12"
            />
          </>
        )} */}
    </div>
  );
}
